import { Text } from '@cretesuite/design-system'
import { IonContent, IonIcon, IonPage } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { arrowForwardCircleOutline, navigateOutline } from 'ionicons/icons'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from 'urql'
import { AddendumBlock } from '../components/assignments/AddendumBlock'
import { AssignmentDateBadge } from '../components/assignments/AssignmentDateBadge'
import { DetailsAckBlock } from '../components/assignments/DetailsAckBlock'
import { InstructionsBlock } from '../components/assignments/InstructionsBlock'
import { InventoryList } from '../components/assignments/InventoryList'
import { LinesOnSiteBlock } from '../components/assignments/LinesOnSiteBlock'
import { TeamList } from '../components/assignments/TeamList'
import {
  OnsiteHeroTimes,
  YardHeroTimes,
} from '../components/assignments/details/HeroTimes'
import { RouteTimeline } from '../components/routes/RouteTimeline'
import {
  AcknowledgeAssignment,
  GetAssignment,
  UpdateRouteWaypoint,
} from '../graphql/assignments'
import {
  buildAssignmentInstructions,
  numberOrderStops,
  selectLinesOnSiteStops,
} from '../helpers/assignmentOrderStops'
import {
  assignmentReportAt,
  hasReportAtWindow,
} from '../helpers/assignmentReportAt'
import { assignmentStatus } from '../helpers/assignmentStatus'
import { visibleCrewAddendum } from '../helpers/crewAddendum'
import { assignmentDate } from '../helpers/datetime'
import { daySuffix } from '../helpers/daySuffix'
import { groupInventoryAcrossOrders } from '../helpers/groupInventoryAcrossOrders'
import { nextRouteAction } from '../helpers/nextRouteAction'
import { submitQueueable } from '../helpers/submitQueueable'
import { teamAssignmentsFor } from '../helpers/teamAssignments'
import { waypointAddress } from '../helpers/waypointAddress'
import { useAssignmentSiteTimes } from '../hooks/useAssignmentSiteTimes'
import { useFeature } from '../hooks/useFeatures'
import { useNavigatePress } from '../hooks/useNavigate'
import { useNetwork } from '../hooks/useNetwork'
import { usePrefetchOrderData } from '../hooks/usePrefetchAssignmentData'
import { useToast } from '../hooks/useToast'
import { getLocale } from '../i18n'
import { StandardLayout } from '../layouts/StandardLayout'

// ── Status → CSS modifier map ────────────────────────────────
const STATUS_CLASS_MAP: Record<string, string> = {
  attention: 'assignment-card--attention',
  confirmed: 'assignment-card--confirmed',
  inProgress: 'assignment-card--in-progress',
  completed: 'assignment-card--completed',
  cancelled: 'assignment-card--cancelled',
}

export const AssignmentDetailPage = () => {
  const { _ } = useLingui()
  const { isOnline } = useNetwork()
  const [presentToast] = useToast()
  const { assignmentId } = useParams<{ assignmentId: string }>()
  const id = Number.parseInt(assignmentId, 10)

  const [result, reexecuteQuery] = useQuery({
    query: GetAssignment,
    variables: { assignmentId: id },
    // Serve the prefetched/normalized cache instantly on (re)mount — Ionic
    // remounts outlet pages on back navigation, and cache-and-network would
    // flash "Loading…" for a frame before the cache resolves. Freshness comes
    // from the prefetch, the post-mutation network-only refetch, and the
    // normalized cache (changes made on the order detail show here too).
    requestPolicy: 'cache-first',
  })

  const [ackResult, ackMutate] = useMutation(AcknowledgeAssignment)
  const [detailsAcked, setDetailsAcked] = useState(false)
  const [ackError, setAckError] = useState<string | null>(null)

  const assignment = result.data?.assignment
  const route = assignment?.route
  const waypoints = route?.waypoints ?? []

  // Cache this assignment's full order/work-ticket/signature flow while online,
  // so it stays available if the connection drops mid-job.
  const orderIds = waypoints
    .map((w) => w.order?.id)
    .filter((oid): oid is number => typeof oid === 'number')
  usePrefetchOrderData(orderIds, isOnline)

  const status = assignment ? assignmentStatus(assignment) : null
  const statusClass = status ? (STATUS_CLASS_MAP[status.slug] ?? '') : ''
  const datetime = assignmentDate(assignment)
  const isCancelled = status?.slug === 'cancelled'
  const needsAcknowledgement = status?.needsAcknowledgement ?? false
  const inProgress = status?.inProgress ?? false

  const hideIfUnacknowledged = useFeature(
    'assignmentHideDetailsTillAcknowledgement',
  )
  const isRouteHiddenUntilAck =
    hideIfUnacknowledged && needsAcknowledgement && !isCancelled && !inProgress

  // Rendered as is: this is where CMP's "View Permits" button arrives
  const addendumMarkdown = visibleCrewAddendum(assignment?.crewAddendumMarkdown)
  // v1: the stops of a cancelled assignment are plain cards
  const buildOrderPath = isCancelled
    ? null
    : (orderId: number) => `/assignments/${assignmentId}/orders/${orderId}`

  // ── Route progress mutation ───────────────────────────
  const [, updateWpMutate] = useMutation(UpdateRouteWaypoint)
  const [wpUpdating, setWpUpdating] = useState(false)

  // ── Map navigation (ported from v1) ───────────────────
  // The full route's stops in order, for multi-point navigation (matches v1,
  // which routes the whole assignment, not just the next stop).
  const routeAddresses = useMemo(
    () =>
      waypoints
        .map(waypointAddress)
        .filter((a): a is NonNullable<typeof a> => a != null),
    [waypoints],
  )
  // Tap navigates with the saved app; long-press re-picks the app (v1 parity)
  const navBtnRef = useNavigatePress<HTMLButtonElement>(routeAddresses)

  // ── Route Progress Button (v1 rule lives in nextRouteAction) ──
  // One "Stop #N" label for the progress button and the instructions headings
  const stopLabel = useCallback(
    (stopNumber: number) => _(msg`Stop #${stopNumber}`),
    [_],
  )

  const telematics = route?.equipment?.telematics
  const routeAction = useMemo(() => {
    const action = nextRouteAction(waypoints, telematics)
    if (!action) return null
    const fallbackLabel =
      action.stopNumber != null ? stopLabel(action.stopNumber) : _(msg`Yard`)
    const wpLabel = action.waypointName ?? fallbackLabel
    const text =
      action.attrToUpdate === 'actualArrivalTime'
        ? _(msg`Arrive at ${wpLabel}`)
        : _(msg`Depart ${wpLabel}`)
    return { ...action, text }
  }, [waypoints, telematics, stopLabel, _])

  const handleRouteProgress = useCallback(async () => {
    if (!routeAction || wpUpdating) return
    setWpUpdating(true)
    try {
      const exec = updateWpMutate({
        data: { [routeAction.attrToUpdate]: DateTime.utc().toISO() },
        where: { id: routeAction.waypointId },
      })
      // The optimistic update already advanced the waypoint; never make the
      // user wait on the network. Offline or a too-slow connection → queue and
      // move on; a responsive connection → refetch to reconcile immediately.
      const outcome = await submitQueueable(exec, { isOnline })
      if (outcome.status === 'queued') {
        presentToast({
          message: _(msg`Saved. It will sync when you're back online.`),
          duration: 3000,
        })
        return
      }
      reexecuteQuery({ requestPolicy: 'network-only' })
    } finally {
      setWpUpdating(false)
    }
  }, [
    routeAction,
    wpUpdating,
    updateWpMutate,
    reexecuteQuery,
    isOnline,
    presentToast,
    _,
  ])

  // ── Derived data ─────────────────────────────────────
  const reportAtDuration = useFeature('assignmentReportAtDuration')
  const reportAtDt = assignmentReportAt(route, reportAtDuration)
  // v1 hides the hero time when there is no report-ahead window: the Depart row already shows it
  const heroReportAt = hasReportAtWindow(reportAtDuration)
    ? reportAtDt
    : undefined

  // Orgs that skip the yard see site-based times instead of Report At / Depart,
  // as long as the first order stop has a scheduled arrival
  const { onsiteAt, concreteAt } = useAssignmentSiteTimes(
    assignment?.usesOnsiteTimes === true,
    waypoints,
  )

  // An inactive assignment lists only its own operator (v1), see teamAssignmentsFor
  const teamAssignments = useMemo(
    () => teamAssignmentsFor(assignment),
    [assignment],
  )

  // The order stops in route order, numbered without the yard (v1)
  const stops = useMemo(() => numberOrderStops(waypoints), [waypoints])

  // Consolidated "what to load" list across every order in the assignment (v1 parity)
  const inventory = useMemo(
    () => groupInventoryAcrossOrders(stops.map((stop) => stop.order)),
    [stops],
  )

  const instructions = useMemo(
    () => buildAssignmentInstructions(stops, stopLabel),
    [stops, stopLabel],
  )

  const linesOnSiteStops = useMemo(() => selectLinesOnSiteStops(stops), [stops])

  const hasLinesOnSite = linesOnSiteStops.length > 0

  const ackLabels = useMemo(() => {
    const labels: string[] = []
    if (hasLinesOnSite) {
      labels.push(_(msg`I am aware there will be lines on site`))
    }
    if (instructions) {
      labels.push(_(msg`I have reviewed the instructions`))
    }
    return labels
  }, [hasLinesOnSite, instructions, _])

  const hasDetailsToConfirm = ackLabels.length > 0 && !isCancelled

  // ── Title ────────────────────────────────────────────
  const headerTitle = (() => {
    if (!datetime) return _(msg`Assignment`)
    const locale = getLocale()
    if (locale === 'es') {
      // Spanish: "Mar, 19 de May"
      return `${datetime.setLocale('es').toFormat('ccc, d ')}de ${datetime.setLocale('es').toFormat('LLL')}`
    }
    // English: "Tue, May 19th"
    return datetime.toFormat('ccc, LLL d') + daySuffix(datetime)
  })()

  // ── Acknowledge handler ──────────────────────────────
  const handleAcknowledge = async () => {
    if (hasDetailsToConfirm && !detailsAcked) {
      setAckError(_(msg`You must confirm you have reviewed the details above.`))
      return
    }
    setAckError(null)
    // Optimistic update already marks it acknowledged; queue without blocking
    // when offline or on a too-slow connection.
    const exec = ackMutate({ where: { id } })
    const outcome = await submitQueueable(exec, { isOnline })
    if (outcome.status === 'queued') {
      presentToast({
        message: _(msg`Saved. It will sync when you're back online.`),
        duration: 3000,
      })
      return
    }
    if (outcome.status === 'error') {
      setAckError(_(msg`An error occurred, please try again.`))
    }
  }

  return (
    <IonPage>
      <StandardLayout
        title={headerTitle}
        defaultBackTo='/assignments'
        headerEndButtons={
          routeAddresses.length > 0 ? (
            <button
              type='button'
              ref={navBtnRef}
              className='assignment-detail__nav-btn'
              aria-label={_(msg`Navigate`)}
            >
              <IonIcon icon={navigateOutline} />
            </button>
          ) : undefined
        }
      >
        <IonContent className='ion-padding'>
          {/* Loading */}
          {result.fetching && !result.data && (
            <div className='text-center'>
              <Text variant='body' className='text-muted-foreground'>
                {_(msg`Loading...`)}
              </Text>
            </div>
          )}

          {/* Error — only when there's no cached data to show (offline reads
              fall back to the persisted cache). */}
          {result.error && !result.data && (
            <div className='assignment-detail__error'>
              <Text variant='body'>{result.error.message}</Text>
            </div>
          )}

          {assignment && route && (
            <div className={`assignment-detail ${statusClass}`}>
              {/* ── Hero Card ── */}
              <div
                className={`assignment-card ${statusClass} assignment-detail__hero`}
              >
                <div className='assignment-card__row'>
                  <AssignmentDateBadge datetime={datetime} />
                  <div className='assignment-card__divider' />

                  {/* Content Column */}
                  <div className='assignment-card__column'>
                    <div className='assignment-detail__hero-content'>
                      {isCancelled && (
                        <div className='assignment-card__status'>
                          {_(msg`Cancelled`)}
                        </div>
                      )}
                      {onsiteAt ? (
                        <OnsiteHeroTimes
                          onsiteAt={onsiteAt}
                          concreteAt={concreteAt}
                          datetime={datetime}
                          equipment={route.equipment}
                        />
                      ) : (
                        <YardHeroTimes
                          reportAt={heroReportAt}
                          datetime={datetime}
                          assignment={assignment}
                          equipment={route.equipment}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Lines on site — inside the hero card so the whole thing is
                    one unified card, like v1. Shown even when cancelled (v1). */}
                <LinesOnSiteBlock stops={linesOnSiteStops} />

                {instructions && !isCancelled && (
                  <InstructionsBlock instructions={instructions} />
                )}

                {needsAcknowledgement && hasDetailsToConfirm && (
                  <DetailsAckBlock
                    labels={ackLabels}
                    acked={detailsAcked}
                    hasError={Boolean(ackError)}
                    onAckedChange={(acked) => {
                      setDetailsAcked(acked)
                      if (acked) setAckError(null)
                    }}
                  />
                )}

                {/* Team — inside the hero card */}
                <TeamList
                  teamAssignments={teamAssignments}
                  currentOperatorId={assignment.operator?.id}
                />

                {/* Inventory — consolidated across all orders, inside the hero
                    card. Not gated by hide-till-ack: v1 only hides the route. */}
                <InventoryList inventory={inventory} />

                {addendumMarkdown && (
                  <AddendumBlock markdown={addendumMarkdown} />
                )}
              </div>

              {/* ── Acknowledge Button (a cancellation is acknowledged too, v1) ── */}
              {needsAcknowledgement && (
                <div className='assignment-detail__ack-wrap'>
                  {ackError && (
                    <Text
                      variant='label'
                      className='assignment-detail__ack-error'
                    >
                      {ackError}
                    </Text>
                  )}
                  <button
                    type='button'
                    className='assignment-detail__ack-btn'
                    onClick={handleAcknowledge}
                    disabled={ackResult.fetching}
                  >
                    {isCancelled
                      ? _(msg`Acknowledge Cancellation`)
                      : _(msg`Acknowledge Assignment`)}
                  </button>
                </div>
              )}

              {/* ── Route Progress Button ── */}
              {routeAction && !isCancelled && !needsAcknowledgement && (
                <div className='route-progress'>
                  <button
                    type='button'
                    className='route-progress__btn'
                    onClick={handleRouteProgress}
                    disabled={wpUpdating}
                  >
                    <IonIcon icon={arrowForwardCircleOutline} />
                    <span>{routeAction.text}</span>
                    {wpUpdating && <span className='route-progress__spinner' />}
                  </button>
                </div>
              )}

              {isRouteHiddenUntilAck ? (
                <div className='assignment-detail__ack-interstitial'>
                  {_(
                    msg`You must first acknowledge your assignment to view order details.`,
                  )}
                </div>
              ) : (
                <>
                  {/* ── Route Timeline ── */}
                  <RouteTimeline
                    waypoints={waypoints}
                    datetime={datetime}
                    buildOrderPath={buildOrderPath}
                  />

                  {waypoints.length === 0 && (
                    <div className='assignment-detail__section'>
                      <div
                        className='text-center'
                        style={{ padding: '2rem 0' }}
                      >
                        <Text variant='body' className='text-muted-foreground'>
                          {_(msg`No orders in this assignment.`)}
                        </Text>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </IonContent>
      </StandardLayout>
    </IonPage>
  )
}

export default AssignmentDetailPage
